import React, { Component } from "react";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { throttle } from "@formant/core/common/throttle";
import { duration } from "@formant/core/model/duration";
import { IDeviceTeleopCustomStreamConfiguration } from "@formant/core/model/IDeviceTeleopCustomStreamConfiguration";
import { IDeviceTeleopRosStreamConfiguration } from "@formant/core/model/IDeviceTeleopRosStreamConfiguration";
import { IRtcStream } from "@formant/core/model/IRtcStream";
import { IRtcStreamMessage } from "@formant/rtc-client/models/IRtcStreamMessage";
import { IRtcStreamPayload } from "@formant/rtc-client/models/IRtcStreamPayload";
import { createRtcStreamMessage } from "@formant/rtc-client/utils/createRtcStreamMessage";

import { operateStore } from "../../../../stores";
import { ITeleopSession } from "../../../../teleop/ITeleopSession";
import { clamp } from "../../../../utils/clamp";
import { Dial, IDialProps } from "../../../common/Dial";
import { ISliderProps, Slider } from "../../../common/Slider";
import * as styles from "./index.scss";

export interface ITeleopSliderProps {
    stream:
        | IDeviceTeleopCustomStreamConfiguration
        | IDeviceTeleopRosStreamConfiguration;
    session?: ITeleopSession;
    debug?: boolean;
}

export type SliderVisualization = "slider" | "dial";

@observer
export class TeleopSlider extends Component<ITeleopSliderProps> {
    @observable
    private value?: number = this.props.stream.defaultValue;
    private numericValue?: number = this.props.stream.defaultValue;
    @observable currentValue?: number;
    private unregister?: () => boolean;

    public componentDidMount() {
        const valueStreamName =
            (this.props.stream as IDeviceTeleopRosStreamConfiguration)
                ?.valueTopicName ||
            (this.props.stream as IDeviceTeleopCustomStreamConfiguration)
                ?.valueStreamName;
        if (valueStreamName) {
            this.unregister = this.props.session?.registerReceiver(
                valueStreamName,
                this.onReceiveCurrentValue,
            );
        }
    }
    public componentWillUnmount() {
        this.unregister?.();
    }
    private throttledSendCommand = throttle(
        () => this.sendCommand({}),
        100 * duration.millisecond,
    );

    public render() {
        const { currentValue } = this;
        const { stream, debug } = this.props;
        const { defaultValue, step, numericControlVisualization } = stream;
        const { armed } = operateStore;

        const value =
            this.value !== undefined
                ? this.value
                : defaultValue !== undefined
                  ? defaultValue
                  : 0;

        const name =
            (stream as IDeviceTeleopRosStreamConfiguration)?.topicName ||
            (stream as IDeviceTeleopCustomStreamConfiguration)?.name;

        const min = stream.min !== undefined ? stream.min : -1;
        const max = stream.max !== undefined ? stream.max : 1;

        const sliderProps: ISliderProps = {
            value,
            min,
            max,
            step,
            currentValue,
            orientation: "horizontal",
            onSliderChange: this.onSliderChange,
            onInputChange: this.onInputChange,
            onInputBlur: this.onInputBlur,
            onMouseUp: this.onMouseUp,
            hasNumberField: true,
            className: styles.slider,
            rangeClassName: styles.range,
            disabled: !armed && !debug,
        };

        const dialProps: IDialProps = {
            value,
            min,
            max,
            step,
            onSliderChange: this.onSliderChange,
            onInputChange: this.onInputChange,
            onInputBlur: this.onInputBlur,
            hasNumberField: true,
            className: styles.dial,
            disabled: !armed && !debug,
        };

        return (
            <div className={styles["teleop-slider"]}>
                <div className={styles.label}>{name}</div>
                {numericControlVisualization === "dial" ? (
                    <Dial {...dialProps} />
                ) : (
                    <Slider {...sliderProps} />
                )}
            </div>
        );
    }

    private onReceiveCurrentValue = (msg: IRtcStreamMessage) => {
        const value = msg.payload.numeric?.value;
        if (value) {
            this.currentValue = value;
        }
    };
    private onSliderChange = (value: number | undefined) => {
        this.value = value;
        this.throttledSendCommand();
    };

    private onInputChange = (value: number | undefined) => {
        let newValue = value;
        if (value) {
            const { stream } = this.props;
            const min = stream.min !== undefined ? stream.min : -1;
            const max = stream.max !== undefined ? stream.max : 1;
            newValue = clamp(value, min, max);
        }

        this.numericValue = newValue;
    };

    private onInputBlur = () => {
        this.value = this.numericValue;
        this.sendCommand({ reliable: true });
    };

    private onMouseUp = () => {
        this.sendCommand({ reliable: true });
    };

    private sendCommand = (config: { reliable?: boolean }) => {
        const { session, stream, debug } = this.props;
        const { reliable } = config;
        const { armed } = operateStore;

        const streamName =
            (stream as IDeviceTeleopRosStreamConfiguration)?.topicName ||
            (stream as IDeviceTeleopCustomStreamConfiguration)?.name;

        if (!streamName || !session || !armed || debug) {
            return;
        }

        const rtcStream: IRtcStream = {
            entityId: session.deviceId,
            streamName,
            streamType: "numeric",
        };

        const msg: IRtcStreamPayload = {
            numeric: { value: this.value || 0 },
        };

        session.send(createRtcStreamMessage(rtcStream, msg), {
            channelLabel: reliable ? "stream.reliable" : "stream.latest-ttl",
        });
    };
}
